import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AsyncResult, useAsyncResult } from 'react-use-async-result';

import { apiConfig } from '@/api';
import { authenticate, authenticateToken, ReviewerAuthInfo } from '@/apiClients/auth';
import { clearAllRegionalAuthErrors } from '@/apiClients/regionalAuthErrors';
import { clearRegionQueryClients } from '@/apiClients/regionQueryClients';
import { clearStaffRegionTokenCache } from '@/apiClients/staffRegionAuth';
import { CapabilityOverrides, getHasAuthFromAppContext, HasAuthFunc } from '@/auth-capabilities';
import { globalLogout } from '@/context/globalLogout';
import { clearRuleFeedbackStorage, resetSessionMemory } from '@/context/sessionClear';
import * as Sentry from '@/sentry';

export interface AppContext {
  // ``initialRes`` describes the auth's initialization state; it will be "pending" until
  // the auth info is first loaded, then remain ``done``. It will never be ``error``.
  initialRes: AsyncResult<unknown>;
  // ``authRes`` describes the auth's current state. It will be "pending" while
  // an authentication request is in progres, and ``done`` or ``error`` when the
  // request is complete.
  authRes: AsyncResult<unknown>;
  authInfo: ReviewerAuthInfo | null;
  // ``otp`` is only sent on the second step, after ``authRes`` has errored
  // with ``MfaRequiredError`` for an MFA-enabled account.
  login: (opts: { email: string; password: string; otp?: string }) => void;
  logout: () => void;
  hasAuth: HasAuthFunc;
  _authCapabilityOverrides: CapabilityOverrides;
  _setAuthCapabilityOverrides: React.Dispatch<React.SetStateAction<CapabilityOverrides>>;
}

type AuthSession = {
  authInfo: ReviewerAuthInfo;
  expiry: number;
};

export const AppCtx = createContext<AppContext>({
  initialRes: AsyncResult.pending(),
  authRes: AsyncResult.empty(),
  authInfo: null,
  login: () => {},
  logout: () => {},
  hasAuth: () => false,
  _authCapabilityOverrides: [],
  _setAuthCapabilityOverrides: () => {},
});

export const useAuth = () => React.useContext(AppCtx);

// "Senior data reviewer" = report/data-reviewer admin, or a global admin. Gates
// elevated meal-review affordances (e.g. Select all, the nutrient column modes).
export const useIsSeniorDataReviewer = (): boolean => {
  const { authInfo } = useAuth();
  return !!(authInfo?.is_admin || authInfo?.is_report_admin || authInfo?.is_data_reviewer_admin);
};

async function loadInitialAuthInfo(): Promise<ReviewerAuthInfo | null> {
  // This token is set by the clinician portal when a clinician clicks a meal
  // to open it in the reviewer.
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    const tokenAauthRes = authenticateToken(token);
    tokenAauthRes.finally(() => {
      const newPath = window.location.pathname + window.location.search.replace(/token=[^&]*&?/, '');
      window.history.replaceState(null, '', newPath);
    });
    return tokenAauthRes;
  }

  // If there is no clinician portal token, try to load the auth info from local
  // storage.
  try {
    const savedAuth = localStorage.getItem('auth_info');
    const { expiry, authInfo } = JSON.parse(savedAuth || '{}') as AuthSession;
    const now = new Date().getTime();
    return expiry && now < expiry ? authInfo : null;
  } catch (e) {
    console.error('Error loading auth:', e);
  }
  return null;
}

const readSavedAccessToken = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem('auth_info') || '{}').authInfo?.access_token;
  } catch {
    return undefined;
  }
};

export { globalLogout };

export const AppContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [_authCapabilityOverrides, _setAuthCapabilityOverrides] = useState<CapabilityOverrides>([]);
  const [authInfo, _setAuthInfo] = useState<ReviewerAuthInfo | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Plain state rather than a second ``useAsyncResult``: binding a promise to
  // that hook from an effect that depends on it swaps in a new pending result
  // on every bind, re-running the effect before the promise can resolve.
  const initialRes = useMemo(() => (isInitialized ? AsyncResult.done(null) : AsyncResult.pending()), [isInitialized]);
  const authRes = useAsyncResult<ReviewerAuthInfo | null>(loadInitialAuthInfo);
  // Tracks the applied token so ``setAuthInfo`` can stay stable across renders.
  const accessTokenRef = useRef<string | undefined>(undefined);

  const setAuthInfo = useCallback((nextAuthInfo: ReviewerAuthInfo | null) => {
    if (!nextAuthInfo) {
      localStorage.removeItem('auth_info');
      apiConfig.accessToken = undefined;
      accessTokenRef.current = undefined;
      clearStaffRegionTokenCache();
      clearRegionQueryClients();
      resetSessionMemory();
      clearRuleFeedbackStorage();
      clearAllRegionalAuthErrors();
      _setAuthInfo(null);
      return;
    }

    if (nextAuthInfo.access_token !== accessTokenRef.current) {
      clearStaffRegionTokenCache();
      clearRegionQueryClients();
      resetSessionMemory();
      clearAllRegionalAuthErrors();
    }
    // A reload that restores the saved session keeps its feedback; any other session starts clean.
    if (readSavedAccessToken() !== nextAuthInfo.access_token) {
      clearRuleFeedbackStorage();
    }
    const now = new Date().getTime();
    localStorage.setItem('auth_info', JSON.stringify({ authInfo: nextAuthInfo, expiry: now + 60 * 60 * 24 * 1000 }));
    apiConfig.accessToken = nextAuthInfo.access_token;
    accessTokenRef.current = nextAuthInfo.access_token;
    _setAuthInfo(nextAuthInfo);

    Sentry.setUser({
      id: '' + nextAuthInfo.reviewer_id,
      username: `User ${nextAuthInfo.reviewer_id}`,
    });
  }, []);

  const { bind: bindAuthRes } = authRes;
  useEffect(() => {
    globalLogout.logout = () => {
      setAuthInfo(null);
      bindAuthRes(Promise.resolve(null));
    };
  }, [setAuthInfo, bindAuthRes]);

  const isAuthSettled = authRes.isDone || authRes.isError;
  useEffect(() => {
    if (isAuthSettled) {
      setIsInitialized(true);
    }
  }, [isAuthSettled]);

  // Keyed on the result itself so auth state is only rewritten when a login,
  // logout or token handoff produces a new result.
  const authResult = authRes.isDone ? authRes.result : undefined;
  useEffect(() => {
    if (authResult === undefined) {
      return;
    }
    setAuthInfo(authResult);
  }, [authResult, setAuthInfo]);

  const value = React.useMemo(() => {
    const res = {
      initialRes,
      authRes,
      authInfo,
      login: (opts: { email: string; password: string; otp?: string }) => {
        authRes.bind(authenticate(opts.email, opts.password, opts.otp));
      },
      logout: () => authRes.bind(Promise.resolve(null)),
      _authCapabilityOverrides,
      _setAuthCapabilityOverrides,
      hasAuth: null as any as ReturnType<typeof getHasAuthFromAppContext>,
    };
    res.hasAuth = getHasAuthFromAppContext(res);
    return res;
  }, [initialRes, authRes, authInfo, _authCapabilityOverrides]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
};
